import { useState, useEffect } from 'react';

export interface AppConfig {
  marquee: any[];
  thresholds: any;
  regimes: any[];
  isLoading: boolean;
  error: string | null;
}

// Global cache to prevent multiple fetches
let globalConfigCache: Omit<AppConfig, 'isLoading' | 'error'> | null = null;
let isFetching = false;
let fetchPromise: Promise<void> | null = null;

export function useAppConfig() {
  const [config, setConfig] = useState<AppConfig>({
    marquee: globalConfigCache?.marquee || [],
    thresholds: globalConfigCache?.thresholds || {},
    regimes: globalConfigCache?.regimes || [],
    isLoading: !globalConfigCache,
    error: null,
  });

  useEffect(() => {
    if (globalConfigCache) return;

    const fetchConfig = async () => {
      try {
        const [dashRes, regimeRes] = await Promise.all([
          fetch('/api/config/dashboard'),
          fetch('/api/config/regimes')
        ]);
        
        let marquee = [];
        let thresholds = {};
        let regimes = [];
        
        if (dashRes.ok) {
          const dashData: any = await dashRes.json();
          marquee = dashData.marquee || [];
          thresholds = dashData.thresholds || {};
        }
        
        if (regimeRes.ok) {
          const regimeData: any = await regimeRes.json();
          regimes = regimeData.regimes || [];
        }

        globalConfigCache = { marquee, thresholds, regimes };
        setConfig({ marquee, thresholds, regimes, isLoading: false, error: null });
      } catch (err: any) {
        console.error('Failed to load app config:', err);
        setConfig(prev => ({ ...prev, isLoading: false, error: err.message }));
      } finally {
        isFetching = false;
      }
    };

    if (!isFetching) {
      isFetching = true;
      fetchPromise = fetchConfig();
    } else if (fetchPromise) {
      fetchPromise.then(() => {
        if (globalConfigCache) {
          setConfig({ ...globalConfigCache, isLoading: false, error: null });
        }
      });
    }
  }, []);

  return config;
}
